<script setup>
import { ref, inject } from 'vue';
import { usePharmacyStore } from '@/stores/pharmacy';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { router } from '@inertiajs/vue3';

const { search, destroy } = usePharmacyStore();
const { accessPharmacy: access } = usePharmacyStore();
const { all } = storeToRefs(usePharmacyStore());

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchPharmacies = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchPharmacies();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchPharmacies();
};

const remove = async (pharmacy) => {
    const confirm = await modals.show('ConfirmationModal', {
        title: trans('label.confirm_delete'),
        prompt: trans('label.are_you_sure_delete'),
    });

    if (confirm) {
        await destroy({ hash: pharmacy.hash });
        searchPharmacies();
    }
};

const openModal = (modal, mode = 'Create', pharmacy = null) => {
    modals.show(modal, {
        props: pharmacy ? {
            pharmacy: pharmacy,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchPharmacies();
    });
};

const accessPharmacy = async (pharmacy) => {
    await access(pharmacy);
    router.visit('/orders');
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title',{ 'what' : 'Pharmacy' }) }}</template>
    <template #actions>
        <StyledButton @click="openModal('PharmaciesModal')">
            <FontAwesomeIcon icon="plus" class="mr-1" /> {{ $t('label.create') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchPharmacies"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'name' },
            { title: $t('label.status'), key: 'status', value: item => `${item.status.name}` },
            { title: $t('label.contact_name'), key: 'contact_name', value: item => `${item.contact_first_name} ${item.contact_last_name}` },
            { title: $t('label.contact_email'), key: 'contact_email' },
            { title: $t('label.contact_phone'), key: 'contact_phone' },
        ]"
        :actions="[
            {
                title: $t('label.Edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('PharmaciesModal', 'Edit', item),
            },
            {
                title: $t('label.Delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
            {
                title: $t('label.Access'),
                icon: 'mdi-arrow-right',
                action: (item) => accessPharmacy(item),
            }
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
